import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, UserCog, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, ROLE_LABELS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { LogoMark } from "../../components/Logo";

function ProfilePanel({ onClose }) {
  const { user, setUserFromGoogle } = useAuth();
  const isPartner = ["builder", "agent"].includes(user?.role);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    company: user?.company || "",
    company_address: user?.company_address || "",
  });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUserFromGoogle(data.user);
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/profile", form);
      setUserFromGoogle(data.user);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (pw.next.length < 6) return toast.error("New password needs at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: user?.has_password ? pw.current : null,
        new_password: pw.next,
      });
      toast.success(user?.has_password ? "Password changed" : "Password created — you can now also sign in with email");
      setPw({ current: "", next: "" });
      await refreshUser();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-panel">
      <div>
        <label className="dash-label">Full name</label>
        <input className="dash-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
      </div>
      <div>
        <label className="dash-label">Contact number (WhatsApp alerts)</label>
        <input className="dash-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" />
      </div>
      <div>
        <label className="dash-label">Office / Company name {isPartner && <span className="text-red-600">*</span>}</label>
        <input className="dash-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="profile-company-input" />
      </div>
      <div>
        <label className="dash-label">Office / Company address {isPartner && <span className="text-red-600">*</span>}</label>
        <textarea rows={2} className="dash-input resize-none" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} data-testid="profile-address-input" />
      </div>
      {isPartner && <p className="text-[11px] text-black/45">Office name and address are mandatory for partners — listings stay in review without them.</p>}
      <button className="dash-btn w-full" onClick={saveProfile} disabled={busy} data-testid="profile-save-button">
        {busy ? "Saving…" : "Save Profile"}
      </button>

      <div className="border-t border-black/10 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">
          {user?.has_password ? "Change password" : "Create a password"}
        </p>
        {!user?.has_password && (
          <p className="mt-1 text-[11px] text-black/45">You joined with Google — set a password to also sign in with email.</p>
        )}
        <div className="mt-3 space-y-3">
          {user?.has_password && (
            <input type="password" className="dash-input" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="password-current-input" />
          )}
          <input type="password" className="dash-input" placeholder="New password (min 6 characters)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="password-new-input" />
          <button className="dash-btn-ghost w-full" onClick={savePassword} disabled={busy} data-testid="password-save-button">
            {user?.has_password ? "Update Password" : "Create Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout({ title, tabs, activeTab, onTabChange, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const nav = (
    <>
      <div className="flex items-center gap-3 border-b border-black/10 px-6 py-5">
        <LogoMark size={30} />
        <div>
          <p className="font-serif text-lg leading-none text-black">Gurgaon Estates</p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-black/50">{ROLE_LABELS[user?.role] || "Desk"}</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4" data-testid="dashboard-nav">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => { onTabChange(t.key); setOpen(false); }}
            data-testid={`tab-${t.key}`}
            className={`mb-1 flex w-full items-center gap-3 px-3 py-2.5 text-left text-[13px] transition-colors ${
              activeTab === t.key ? "bg-black text-white" : "text-black/60 hover:bg-black/5 hover:text-black"
            }`}
          >
            <t.icon size={15} />
            {t.label}
            {t.badge > 0 && <span className="ml-auto bg-gold px-1.5 py-0.5 font-mono text-[9px] text-ink">{t.badge}</span>}
          </button>
        ))}
      </nav>
      <div className="border-t border-black/10 p-4">
        <p className="truncate px-2 text-sm font-medium text-black" data-testid="dashboard-user-name">{user?.name}</p>
        <p className="truncate px-2 text-[11px] text-black/45">{user?.email}</p>
        <button onClick={() => setProfileOpen(true)} className="dash-btn-ghost mt-3 w-full" data-testid="profile-edit-button">
          <UserCog size={12} /> Edit Profile
        </button>
        <div className="mt-2 flex gap-2">
          <Link to="/" className="dash-btn-ghost flex-1" data-testid="dashboard-view-site">Site</Link>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="dash-btn-ghost flex-1"
            data-testid="dashboard-logout-button"
          >
            <LogOut size={12} /> Exit
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-paper text-black" data-testid={`${user?.role}-dashboard`}>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-black/10 bg-white lg:flex">{nav}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white">{nav}</aside>
        </div>
      )}
      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-black/10 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="dashboard-menu-toggle" aria-label="Menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="font-serif text-2xl text-black" data-testid="dashboard-title">{title}</h1>
          </div>
          {user?.status === "pending" && (
            <span className="bg-amber-100 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-900" data-testid="pending-badge">
              Awaiting approval
            </span>
          )}
        </header>
        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>

      {profileOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="profile-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setProfileOpen(false)} />
          <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border border-black/10 bg-white p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-black">Your Profile</h2>
              <button onClick={() => setProfileOpen(false)} data-testid="profile-close-button" aria-label="Close"><X size={18} /></button>
            </div>
            <ProfilePanel onClose={() => setProfileOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export function StatCard({ label, value, sub, testid }) {
  return (
    <div className="dash-card p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">{label}</p>
      <p className="mt-2 font-serif text-4xl text-black">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-black/45">{sub}</p>}
    </div>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="dash-card p-12 text-center">
      <p className="font-serif text-2xl text-black">{title}</p>
      {hint && <p className="mt-2 text-sm text-black/45">{hint}</p>}
    </div>
  );
}

export const Th = ({ children }) => (
  <th className="border-b border-black/10 px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-black/45">{children}</th>
);

export const Td = ({ children, className = "" }) => (
  <td className={`border-b border-black/5 px-4 py-3 align-top text-[13px] text-black/80 ${className}`}>{children}</td>
);
